import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { openPaySheet } from "@/components/pay/PaySheet";
import SiteMarkSwitch from "@/components/pay/SiteMarkSwitch";
import type { StoryboardProject } from "./types";
import { requestBoardRender, requestBoardRenderCancel, requestBoardRenderConfirm } from "./studioBus";

// RenderGate — what a Render click answers with, drawn where the click was made
// (the board's Render row, and the phone sheet's footer). The price question
// used to open only in the chat, at the bottom of its scroll box: on a reopened
// board it landed out of sight and the button looked dead (owner report
// 2026-10-02). The flow still owns the money: this only shows what it
// published and sends Confirm / Keep editing back over the studio bus.

/** The Render click itself. `unheard` turns true when no flow answered it. */
export function useBoardRender() {
  const [unheard, setUnheard] = useState(false);
  return { unheard, render: () => setUnheard(!requestBoardRender()) };
}

export default function RenderGate({ board, unheard, takeFocus = true }: { board: StoryboardProject; unheard: boolean; takeFocus?: boolean }) {
  const { t } = useTranslation();
  const keepRef = useRef<HTMLButtonElement | null>(null);
  const asking = !!board.renderAsk;
  // Keyboard lands on "Keep editing", never on Confirm: Enter pressed twice on
  // Render (or held) must not pass the price unread. No scrolling (the board is
  // a pannable canvas). Retried for a few frames: the canvas keeps a card
  // hidden while it measures it again, and a hidden button cannot take focus.
  // `takeFocus` false: another copy of the question is the one on screen (the
  // phone sheet over the canvas).
  useEffect(() => {
    if (!asking || !takeFocus) return;
    let frame = 0;
    let tries = 0;
    const focus = () => {
      const el = keepRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      if (document.activeElement !== el && ++tries < 30) frame = requestAnimationFrame(focus);
    };
    focus();
    return () => cancelAnimationFrame(frame);
  }, [asking, takeFocus]);

  if (asking) {
    const cost = board.renderCostCredits ?? 0;
    const balance = board.creditBalance ?? null;
    const short = balance !== null && balance < cost;
    return (
      <div data-testid="studio-render-confirm" role="alertdialog" aria-label={t("corrido.confirmCredits")}
        style={{ marginTop: 10, borderRadius: 12, border: "1px solid #e3b341", background: "rgba(227,179,65,.1)", padding: 12, display: "grid", gap: 8, maxWidth: 560 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{t("corrido.confirmCredits")}</div>
        <div style={{ fontSize: 13, color: "#e2e8f0", lineHeight: 1.6 }}>
          {t("corrido.renderCostsLead")} <b style={{ color: "#e3b341" }}>{t("corrido.renderCostsCredits", { count: cost.toLocaleString() })}</b>.
          {" "}{t("corrido.yourBalance")} <b style={{ color: "#e3b341" }}>{balance === null ? "—" : balance.toLocaleString()}</b>
          {balance !== null && (short
            ? <> — <b style={{ color: "#f87171" }}>{t("corrido.notEnoughTopUp")}</b>.</>
            : <> → <b style={{ color: "#34d399" }}>{t("corrido.afterBalance", { count: (balance - cost).toLocaleString() })}</b>.</>)}
          {" "}{t("corrido.refundNote")}
        </div>
        <SiteMarkSwitch testId="studio-site-mark" style={{ color: "#e2e8f0" }} />
        {/* Right-aligned: the canvas zoom pill floats over the bottom-left corner. */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button type="button" data-testid="studio-render-confirm-run" disabled={short} onClick={() => requestBoardRenderConfirm()}
            style={{ borderRadius: 8, border: "none", background: "linear-gradient(135deg,#ec4899,#8b5cf6)", color: "#fff", fontWeight: 700, letterSpacing: 1, padding: "9px 16px", fontSize: 13, cursor: short ? "default" : "pointer", opacity: short ? 0.5 : 1 }}>
            {t("corrido.confirmAndRun")}
          </button>
          {short && (
            // The pay sheet over the board (Studio Blueprint 04), the covering pack picked.
            <button type="button" data-testid="studio-render-buy" onClick={() => openPaySheet({ need: cost, have: balance ?? 0, label: t("corrido.payForRender") })}
              style={{ borderRadius: 8, border: "1px solid #e3b341", background: "transparent", color: "#e3b341", padding: "9px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{t("corrido.buyCredits")}</button>
          )}
          <button type="button" ref={keepRef} data-testid="studio-render-keep-editing" onClick={() => requestBoardRenderCancel()}
            style={{ borderRadius: 8, border: "1px solid #2a3648", background: "transparent", color: "#94a3b8", padding: "9px 14px", fontSize: 13, cursor: "pointer" }}>
            {t("corrido.keepEditing")}
          </button>
        </div>
      </div>
    );
  }
  // Why the last click did not start anything, in the flow's own words.
  if (board.renderNotice) {
    return <div data-testid="studio-render-notice" role="alert" style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: "#f87171", lineHeight: 1.5 }}>{board.renderNotice}</div>;
  }
  // Nothing answered the click: the flow that owns this run is gone. Reopening
  // the run puts it back, with this same storyboard.
  if (unheard) {
    return (
      <div data-testid="studio-render-unheard" role="alert" style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: "#f87171", lineHeight: 1.5 }}>
        {t("studio.timeline.renderUnheard")}{" "}
        {board.runId && <a href={`/?resume_corrido=${encodeURIComponent(board.runId)}#director-console`} style={{ color: "#38bdf8", textDecoration: "underline" }}>{t("studio.timeline.renderUnheardReopen")}</a>}
      </div>
    );
  }
  return null;
}
